// App-wide UI state — Zustand. M1'de minimal; M2+ auth/user state buraya taşınacak.
// M9-S3: UI density tercihi (form fill comfortable/compact).
// 2026-04-24: Sidebar collapsed tercihi (AppShell).
import { create } from "zustand";

export type UiDensity = "comfortable" | "compact";

const DENSITY_STORAGE_KEY = "mahir.ui.density";
const SIDEBAR_STORAGE_KEY = "mahir.ui.sidebar-collapsed";
// Form doldurma ekranı panel tercihleri — split-window'da 3 sabit kolon form
// alanını daraltıyordu (müşteri talebi); iki yan panel de daraltılabilir.
const SECTION_NAV_STORAGE_KEY = "mahir.ui.section-nav-collapsed";
const MISSING_PANEL_STORAGE_KEY = "mahir.ui.missing-panel-collapsed";

function readDensity(): UiDensity {
  if (typeof window === "undefined") return "comfortable";
  const v = window.localStorage.getItem(DENSITY_STORAGE_KEY);
  return v === "compact" ? "compact" : "comfortable";
}

function readSidebarCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
}

function readFlag(key: string): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(key) === "1";
}

interface AppState {
  bootedAt: number;
  uiDensity: UiDensity;
  setUiDensity: (d: UiDensity) => void;
  toggleUiDensity: () => void;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  // Form doldurma ekranı yan panelleri (split-window daralması — müşteri talebi)
  sectionNavCollapsed: boolean;
  toggleSectionNav: () => void;
  missingPanelCollapsed: boolean;
  toggleMissingPanel: () => void;
  // Müşteri Katılımcı Rolü (Task 8, design-ref Mobil kararı D7): client formu
  // daha dar/sade açılsın diye iki yan panel varsayılan kapalı başlar — AMA
  // SADECE kullanıcı daha önce hiç dokunmadıysa (localStorage'da açık kayıt
  // yoksa). Persist ETMEZ (localStorage'a yazmaz) — internal kullanıcının aynı
  // tarayıcıdaki tercihini kalıcı olarak bozmaz; her FormFill mount'unda
  // "hâlâ dokunulmamış mı" kontrolü tekrar çalışır, dokunulmuşsa normal toggle
  // state'i (persisted) aynen kullanılır.
  applyClientPanelDefaults: () => void;
  // Finar UX P3 Ö6: ≤1024 px'te iki yan panel daraltılmış açılır ve açılınca formu itmez
  // (overlay). Dar kipte panel durumu PERSIST EDİLMEZ — geniş ekran tercihi bozulmaz;
  // geniş ekrana dönünce kalıcı tercih localStorage'dan geri okunur.
  narrowFormPanels: boolean;
  setNarrowFormPanels: (narrow: boolean) => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  bootedAt: Date.now(),
  uiDensity: readDensity(),
  setUiDensity: (d) => {
    if (typeof window !== "undefined") window.localStorage.setItem(DENSITY_STORAGE_KEY, d);
    set({ uiDensity: d });
  },
  toggleUiDensity: () => {
    const next: UiDensity = get().uiDensity === "compact" ? "comfortable" : "compact";
    if (typeof window !== "undefined") window.localStorage.setItem(DENSITY_STORAGE_KEY, next);
    set({ uiDensity: next });
  },
  sidebarCollapsed: readSidebarCollapsed(),
  toggleSidebar: () => {
    const next = !get().sidebarCollapsed;
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? "1" : "0");
    }
    set({ sidebarCollapsed: next });
  },
  sectionNavCollapsed: readFlag(SECTION_NAV_STORAGE_KEY),
  toggleSectionNav: () => {
    const next = !get().sectionNavCollapsed;
    if (typeof window !== "undefined" && !get().narrowFormPanels) {
      window.localStorage.setItem(SECTION_NAV_STORAGE_KEY, next ? "1" : "0");
    }
    set({ sectionNavCollapsed: next });
  },
  missingPanelCollapsed: readFlag(MISSING_PANEL_STORAGE_KEY),
  toggleMissingPanel: () => {
    const next = !get().missingPanelCollapsed;
    if (typeof window !== "undefined" && !get().narrowFormPanels) {
      window.localStorage.setItem(MISSING_PANEL_STORAGE_KEY, next ? "1" : "0");
    }
    set({ missingPanelCollapsed: next });
  },
  applyClientPanelDefaults: () => {
    if (typeof window === "undefined") return;
    const patch: Partial<AppState> = {};
    if (window.localStorage.getItem(SECTION_NAV_STORAGE_KEY) === null) {
      patch.sectionNavCollapsed = true;
    }
    if (window.localStorage.getItem(MISSING_PANEL_STORAGE_KEY) === null) {
      patch.missingPanelCollapsed = true;
    }
    if (Object.keys(patch).length > 0) set(patch);
  },
  narrowFormPanels: false,
  setNarrowFormPanels: (narrow) => {
    if (narrow) {
      set({ narrowFormPanels: true, sectionNavCollapsed: true, missingPanelCollapsed: true });
      return;
    }
    if (!get().narrowFormPanels) return;
    set({
      narrowFormPanels: false,
      sectionNavCollapsed: readFlag(SECTION_NAV_STORAGE_KEY),
      missingPanelCollapsed: readFlag(MISSING_PANEL_STORAGE_KEY),
    });
  },
}));
